/* eslint-disable react/no-array-index-key */
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { MdInfo, MdOutlineWarning } from 'react-icons/md';
import { LineBreak, TooltipText, TooltipWrapper, IconWrapper } from './styles';

interface TooltipStatusProps {
  tipo: 'INF' | 'BLOQ';
  mensagem: string;
}

/**
 * TooltipStatusValidacao
 * Tooltip customizado que renderiza via portal no document.body para evitar clipping
 * dentro de containers com overflow/scroll (como TableContainer do MUI).
 *
 * - Aparência mantida via styled-components.
 * - Posição ancorada ao ícone; atualiza em scroll/resize.
 */
export const TooltipStatusValidacao: React.FC<TooltipStatusProps> = ({
  tipo,
  mensagem,
}) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const items = useMemo(
    () => ({
      INF: { icon: MdInfo, color: '#ffbf00' },
      BLOQ: { icon: MdOutlineWarning, color: '#e83a3a' },
    }),
    [],
  );

  const { color } = items[tipo];
  const Icon = items[tipo].icon;

  const recompute = useCallback(() => {
    if (!wrapperRef.current) return;
    const r = wrapperRef.current.getBoundingClientRect();
    // GAP abaixo e à direita do ícone
    const GAP_Y = 8;
    const GAP_X = 10;
    setCoords({
      top: r.bottom + GAP_Y,
      left: r.left + r.width + GAP_X,
    });
  }, []);

  const handleEnter = useCallback(() => {
    recompute();
    setOpen(true);
  }, [recompute]);

  const handleLeave = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => recompute();
    // true: captura scroll de containers internos (ex.: TableContainer)
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, recompute]);

  const messageNodes = useMemo(() => {
    const lines = mensagem.split('\n');
    return lines.map((line, idx) => {
      const clean = line.trim();
      const isTitle = clean !== '' && /^(inf|imp)/i.test(clean);
      return (
        <Fragment key={`${clean}-${idx}`}>
          {isTitle ? (
            <p
              style={{
                borderBottom: `0.0938rem solid ${color}`,
                fontWeight: 'bold',
              }}
            >
              {line}
            </p>
          ) : (
            <span>{line}</span>
          )}
          {idx !== lines.length - 1 && <LineBreak />}
        </Fragment>
      );
    });
  }, [mensagem, color]);

  return (
    <TooltipWrapper
      ref={wrapperRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      aria-haspopup="dialog"
      aria-expanded={open}
    >
      <IconWrapper>
        <Icon color={color} />
      </IconWrapper>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <TooltipText
            color={color}
            style={{ top: coords.top, left: coords.left }}
          >
            {messageNodes}
          </TooltipText>,
          document.body,
        )}
    </TooltipWrapper>
  );
};
